import { Heading, VStack, Button, Box, Table, Thead, Tbody, Tr, Th, Td, useToast, useColorModeValue, HStack, IconButton, Text } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash } from "react-icons/fi";

interface Blog {
  blog_id: number;
  title: string;
  slug: string;
  author_name?: string;
  published_at?: string;
  is_active: boolean;
  sequence?: number;
}

type OperationMode = "none" | "resequence";

const B2CBlogManager = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const navigate = useNavigate();
  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");
  const [operationMode, setOperationMode] = useState<OperationMode>("none");
  const [needsResequence, setNeedsResequence] = useState(false);

  const fetchBlogs = useCallback(async () => {
    try {
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const response = await axios.get(`${apiurl}/blogs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('b2c_token')}` },
      });
      setBlogs(response.data);
    } catch (error) {
      console.error("Failed to load Blogs", error);
    }
  }, []);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  useEffect(() => {
    if (needsResequence) {
      handleSequenceCommit();
      setNeedsResequence(false);
    }
  }, [blogs, needsResequence]);

  const handleDeleteBlog = async (blog_id: number) => {
    try {
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      await axios.delete(`${apiurl}/blog/${blog_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('b2c_token')}` },
      });
      await fetchBlogs();
      setNeedsResequence(true);
      toast({ title: "Blog deleted successfully", status: "success" });
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error deleting blog", status: "error" });
    }
  };

  const handleAddNewBlog = () => {
    navigate(`/practice/app/b2c-blog-manager/0/review-blog`);
  };

  const handleReviewBlog = (blog_id: number) => {
    navigate(`/practice/app/b2c-blog-manager/${blog_id}/review-blog`);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0 || operationMode !== "resequence") return;
    const updated = [...blogs];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setBlogs(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === blogs.length - 1 || operationMode !== "resequence") return;
    const updated = [...blogs];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    setBlogs(updated);
  };

  const handleSequenceCommit = async () => {
    const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const token = localStorage.getItem("b2c_token");
    try {
      const updatedSequence = blogs.map((b, i) => ({ blog_id: b.blog_id, sequence_number: i + 1 }));
      await axios.put(`${apiurl}/blogs/reorder`, updatedSequence, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "New order committed", status: "success" });
      await fetchBlogs();
      setOperationMode("none");
    } catch (error) {
      toast({ title: "Failed to resequence blogs", status: "error" });
    }
  };

  const handleCancelOperation = () => {
    fetchBlogs();
    setOperationMode("none");
  };

  return (
    <Box as="main" mx="auto" p={10}>
      <VStack>
        <Heading size="md">B2C Blog Manager</Heading>
      </VStack>

      <Box mt={4}>
        <Text fontWeight="bold">Existing Blogs</Text>
        <HStack mt={2}>
          <Button borderRadius="sm" fontSize="sm" fontWeight="normal" onClick={handleAddNewBlog} isDisabled={operationMode === "resequence"}>
            + Blog
          </Button>
          <Button
            borderRadius="sm" fontSize="sm" fontWeight="normal"
            onClick={() => setOperationMode("resequence")}
            isDisabled={operationMode === "resequence"}
          >
            Resequence Blogs
          </Button>
        </HStack>

        {operationMode !== "none" && (
          <HStack mt={4} spacing={3}>
            <Button size="sm" colorScheme="blue" onClick={handleSequenceCommit}>Commit</Button>
            <Button size="sm" variant="outline" colorScheme="red" onClick={handleCancelOperation}>Cancel</Button>
          </HStack>
        )}
      </Box>

      <Box mt={4}>
        <Table mt={4} borderColor="gray.300" borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>ID</Th>
              <Th>Sequence</Th>
              <Th>Title</Th>
              <Th>Slug</Th>
              <Th>Author</Th>
              <Th>Published</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">
            {blogs.map((blog, index) => (
              <Tr key={blog.blog_id}>
                <Td>{blog.blog_id}</Td>
                <Td>{blog.sequence}</Td>
                <Td>{blog.title}</Td>
                <Td>{blog.slug}</Td>
                <Td>{blog.author_name}</Td>
                <Td>{blog.published_at ? new Date(blog.published_at).toLocaleDateString() : "-"}</Td>
                <Td>{blog.is_active ? "Active" : "Inactive"}</Td>
                <Td>
                  {operationMode === "resequence" && (
                    <>
                      <IconButton size="xs" icon={<ChevronUpIcon />} aria-label="Move Up" onClick={e => { e.stopPropagation(); handleMoveUp(index); }} />
                      <IconButton size="xs" icon={<ChevronDownIcon />} aria-label="Move Down" onClick={e => { e.stopPropagation(); handleMoveDown(index); }} />
                    </>
                  )}
                  {operationMode === "none" && (
                    <>
                      <Tooltip label="Edit">
                        <Button size="sm" onClick={() => handleReviewBlog(blog.blog_id)} mr={2}>
                          <FiEdit />
                        </Button>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <Button size="sm" colorScheme="red" onClick={() => {
                          if (window.confirm("Are you sure you want to delete this blog?")) handleDeleteBlog(blog.blog_id);
                        }}>
                          <FiTrash />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default B2CBlogManager;